import * as React from "react";
import { Modal, Form, Button, Grid, Divider } from "semantic-ui-react";

import { FileUpload } from "@teamprevent/common/fileUploads";
import { acceptableExtensions } from "@teamprevent/common/acceptableExtensions";

import { ImmunisationAndEvidenceCreateModel } from "../../immunisations/model";
import { ImmunisationFields } from "../../immunisations/components/ImmunisationFields";

interface Props {
    employeeRecordId: string;
    onSave: (immunisation: ImmunisationAndEvidenceCreateModel) => void;
    onClose: () => void;
}

const CreateImmunisationModal: React.FunctionComponent<Props> = (props) => {
    const [showErrors, setShowErrors] = React.useState(false);
    const [model, setModel] = React.useState<ImmunisationAndEvidenceCreateModel>({
        immunisationTypeName: null,
        dateCompleted: null,
        recordType: null,
        completedByName: null,
        evidence: [],
        identityValidation: null,
        notes: null,
        outcome: null
    });

    const onSave = async () => {
        setShowErrors(true);
        if (!model.immunisationTypeName ||
            !model.dateCompleted ||
            !model.recordType ||
            !model.outcome ||
            model.identityValidation === null ||
            model.identityValidation === undefined ||
            !model.completedByName) {
            return;
        }

        await props.onSave(model);
        props.onClose();
    };

    const updateProperty = <
        TKey extends keyof ImmunisationAndEvidenceCreateModel = keyof ImmunisationAndEvidenceCreateModel,
        TValue = ImmunisationAndEvidenceCreateModel[TKey]
    >(prop: TKey, value: TValue) => {
        setModel(prevModel => ({
            ...prevModel,
            [prop]: value
        }));
    };

    const changeEvidence = (index: number, blobId: string, fileName: string) => {
        setModel(prevModel => ({
            ...prevModel,
            evidence: [
                ...prevModel.evidence.slice(0, index),
                { blobId, fileName },
                ...prevModel.evidence.slice(index + 1)
            ]
        }));
    };

    const removeEvidence = (index: number) => {
        setModel(prevModel => ({
            ...prevModel,
            evidence: [
                ...prevModel.evidence.slice(0, index),
                ...prevModel.evidence.slice(index + 1)
            ]
        }));
    };

    const addEvidence = () => {
        setModel(prevModel => ({
            ...prevModel,
            evidence: [
                ...prevModel.evidence,
                { blobId: null, fileName: null }
            ]
        }));
    };

    return (
        <>
            <Modal open>
                <Modal.Content>
                    <Form>
                        <ImmunisationFields immunisation={model} updateProperty={updateProperty} showErrors={showErrors} />
                        <Form.Field>
                            <label>Evidence</label>
                            {model.evidence && model.evidence.map((evidence, index) =>
                                (<React.Fragment key={index}>
                                    <Grid columns="2">
                                        <Grid.Column>
                                            {evidence.blobId
                                                ? <a
                                                    key={index}
                                                    // eslint-disable-next-line max-len
                                                    href={`/api/employeeRecord/${props.employeeRecordId}/immunisations/evidence/${evidence.blobId}?fileName=${evidence.fileName}`}
                                                >
                                                    <Button content={evidence.fileName} type="button" />
                                                </a>
                                                : <FileUpload
                                                    apiUrl={`/api/employeeRecord/${props.employeeRecordId}/immunisations/evidence`}
                                                    onFileChange={(blobId, fileName) => changeEvidence(index, blobId, fileName)}
                                                    fileName={evidence.fileName}
                                                    acceptExtensions={acceptableExtensions}
                                                />}
                                        </Grid.Column>
                                        <Grid.Column textAlign="right" verticalAlign="middle">
                                            <Button
                                                className="remove-evidence-button"
                                                content="Remove"
                                                type="button"
                                                onClick={() => removeEvidence(index)}
                                                floated="right"
                                            />
                                        </Grid.Column>
                                    </Grid>
                                    <Divider />
                                </React.Fragment>))
                            }
                            <Form.Button type="button" content="Add Evidence" onClick={addEvidence} />
                        </Form.Field>
                    </Form>
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={props.onClose}>Cancel</Button>
                    <Button onClick={onSave}>Save</Button>
                </Modal.Actions>
            </Modal>
        </>
    );
};

export { CreateImmunisationModal };
